import {
  Box,
  Typography,
  Stack,
  StackProps,
  debounce
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import ReactECharts from "echarts-for-react";
import { ReactNode, useEffect, useMemo, useState } from "react";

import WidgetWraper from "components/Common/WidgetWraper";
import LoaderComponent from "components/Loader";
import { InfoTooltip, TooltipLabel } from "../InfoTooltip";

import { buildSegmentedBarChartOptions } from "../chartWidgetUtility";
import HighlightedText from "components/GlobalSearch/HighlightText";

interface SegmentedBarChartProps {
  title: string;
  subTitle?: string;
  categories: string[];
  series: any[];
  isLoading?: boolean;
  xAxisLabel?: string;
  showBarLabel?: boolean;
  showLineLabel?: boolean;
  isCost?: boolean;

  hasTooltip?: boolean;
  tooltipLabel?: TooltipLabel;
  orientation?: "vertical" | "horizontal";
  mode?: "stacked" | "grouped";
  colorScheme?: string[];
  showGridLines?: boolean;
  containerProps?: StackProps;
  onBarClick?: (params: any) => void;

  hasFilter?: boolean;
  childrenContainerProps?: StackProps;
  children?: ReactNode;
  minTitleHeight?: `${number}px`;
  headerStackProps?: StackProps;
}

export default function SegmentedBarChart({
  title,
  subTitle,
  categories,
  series,
  isLoading = false,
  xAxisLabel,
  showBarLabel = true,
  showLineLabel = true,
  isCost = false,

  hasTooltip = true,
  tooltipLabel,

  orientation = "vertical",
  mode = "grouped",

  colorScheme,
  showGridLines = false,

  containerProps,
  onBarClick,

  hasFilter = false,
  childrenContainerProps,
  children,
  minTitleHeight = "38px",
  headerStackProps
}: Readonly<SegmentedBarChartProps>) {
  const theme = useTheme();
  const colors = theme.palette;
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const chartOptions = useMemo(
    () =>
      buildSegmentedBarChartOptions(categories, series, {
        orientation,
        mode,
        colors,
        colorScheme,
        showGridLines,
        xAxisLabel,
        showBarLabel,
        showLineLabel,
        isCost,
        showLegendOnTop: Boolean(subTitle)
      }),
    [
      categories,
      series,
      orientation,
      mode,
      colorScheme,
      showGridLines,
      colors,
      xAxisLabel,
      showBarLabel,
      showLineLabel,
      isCost,
      subTitle
    ]
  );

  useEffect(() => {
    const handleResize = debounce(() => setWindowSize(window.innerWidth), 200);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <WidgetWraper>
      <Box
        sx={{ backgroundColor: colors.widget.background.card }}
        {...containerProps}
      >
        <Stack
          gap={2}
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          minHeight={minTitleHeight}
          mb={subTitle ? 1 : 2}
          {...headerStackProps}
        >
          <Stack direction="column" gap={2} mb={2}>
            <Stack direction="row" alignItems="center">
            <Typography variant="widgetTitleNew" sx={{ color: colors.widget.text.title }}>
              <HighlightedText text={title} />
            </Typography>

              {hasTooltip && tooltipLabel && <InfoTooltip label={tooltipLabel} />}
            </Stack>

            {subTitle && (
              <Typography
                variant="bodyText"
                sx={{ color: colors.widget.text.title }}
              >
                {subTitle}
              </Typography>
            )}
          </Stack>

          {hasFilter && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={2}
              alignItems={{ xs: "flex-end", md: "center" }}
              justifyContent={{ xs: "flex-end" }}
              width={{ xs: "100%", md: "auto" }}
              pr={{ xs: 1, md: "initial" }}
              my={{ xs: 2, md: 0 }}
              {...childrenContainerProps}
            >
              {children}
            </Stack>
          )}
        </Stack>

        <Box sx={{ width: "100%", height: 350 }} key={windowSize}>
          {!isLoading ? (
            <ReactECharts
              option={chartOptions}
              style={{ width: "100%", height: "100%" }}
              onEvents={{
                click: (params: any) => onBarClick?.(params)
              }}
            />
          ) : (
            <LoaderComponent pt={20} />
          )}
        </Box>
      </Box>
    </WidgetWraper>
  );
}